import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { IDefaultSelect } from '@interfaces/default-select';
import { forkJoin, ReplaySubject } from 'rxjs';
import { enumPackageType, enumPalletStandard } from '@data-layer/enums/lcl';
import { debounceTime, map, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { enumLengthUnit, enumWeightUnit } from '@data-layer/enums/common';
import { PackageData } from '@app/modules/lcl-shipping/types';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import {
  PALLET_EUR_1_IMPERIAL_LENGTH,
  PALLET_EUR_1_IMPERIAL_WIDTH,
  PALLET_EUR_1_METRIC_LENGTH,
  PALLET_EUR_1_METRIC_WIDTH,
  PALLET_EUR_2_IMPERIAL_LENGTH,
  PALLET_EUR_2_IMPERIAL_WIDTH,
  PALLET_EUR_2_METRIC_LENGTH,
  PALLET_EUR_2_METRIC_WIDTH,
  PALLET_GMA_IMPERIAL_LENGTH,
  PALLET_GMA_IMPERIAL_WIDTH,
  PALLET_GMA_METRIC_LENGTH,
  PALLET_GMA_METRIC_WIDTH,
} from '@constants/constants';
import { CustomValidator } from '@helpers/custom-validator';

@Component({
  selector: 'app-lcl-shipping-package-edit',
  templateUrl: './lcl-shipping-package-edit.component.html',
  styleUrls: ['./lcl-shipping-package-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclShippingPackageEditComponent implements OnInit, OnDestroy {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public index: number;
  @Input() public isLastest: boolean;
  @Input() public isSidebar: boolean;
  @Input() public isAdminView: boolean;
  @Input() public isExternal: boolean;
  @Input() public isShowClear: boolean;
  @Output() public removePackage: EventEmitter<number> = new EventEmitter<number>();
  @Output() public formUpdated: EventEmitter<PackageData> = new EventEmitter<PackageData>();

  public numberInputData: InputTypeMap = INPUT_TYPES_MAP.INTEGER;
  public decimalInputData: InputTypeMap = INPUT_TYPES_MAP.DECIMAL;
  public errors: IErrorForm = {
    required: 'LABELS.REQUIRED',
    min: 'LABELS.REQUIRED',
  };
  public typeErrors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
  };

  public typeSelect: IDefaultSelect[] = [];
  public palletStandardSelect: IDefaultSelect[] = [];
  public weightUnits: enumWeightUnit[] = [enumWeightUnit.KG, enumWeightUnit.LB];
  public lengthUnits: enumLengthUnit[] = [enumLengthUnit.CENTIMETER, enumLengthUnit.INCH];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private translateService: TranslateService, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.getTypeSelect();
    this.getPalletTypeSelect();

    this.formGroup.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.formUpdated.emit(this.formGroup.value);
    });

    this.formGroup
      .get('type')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: enumPackageType) => {
        if (val === enumPackageType.PALLET) {
          this.formGroup.get('palletStandard').setValue(undefined, { emitEvent: false });
          this.formGroup.get('palletStandard').setValidators(CustomValidator.notUndefined());
          this.formGroup.get('palletStandard').updateValueAndValidity();
        } else {
          this.formGroup.get('palletStandard').setValue(null, { emitEvent: false });
          this.formGroup.get('palletStandard').setValidators(null);
          this.formGroup.get('palletStandard').updateValueAndValidity();
        }

        this.cdr.markForCheck();
      });

    this.formGroup
      .get('palletStandard')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: enumPalletStandard) => {
        this.setDimensions(val);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onRemoveQuote(): void {
    this.removePackage.emit(this.index);
  }

  public onClear(): void {
    this.formGroup.get('type').setValue(enumPackageType.BOX);
    this.formGroup.get('dimensions').reset();
    this.formGroup.get('quantity').reset();
    this.formGroup.get('weight').reset();
  }

  public forceUpdateForm(): void {
    this.formGroup.markAsTouched();
    this.formGroup.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  public decreaseQuantity(): void {
    if (Number(this.formGroup.get(['quantity']).value) > 1) {
      this.formGroup.get(['quantity']).setValue(Number(this.formGroup.get(['quantity']).value) - 1);
    }
  }

  public increaseQuantity(): void {
    this.formGroup.get(['quantity']).setValue(Number(this.formGroup.get(['quantity']).value) + 1);
  }

  public get isShowBoxInput(): boolean {
    return this.formGroup.get('type').value === enumPackageType.BOX;
  }

  public get isShowPalletInput(): boolean {
    return this.formGroup.get('type').value === enumPackageType.PALLET;
  }

  public get isShowPalletStandardInput(): boolean {
    return (
      this.formGroup.get('palletStandard').value === undefined ||
      (this.formGroup.get('type').value === enumPackageType.PALLET &&
        [enumPalletStandard.GMA, enumPalletStandard.EUR_1, enumPalletStandard.EUR_2].includes(this.formGroup.get('palletStandard').value))
    );
  }

  private getTypeSelect(): void {
    forkJoin(
      [enumPackageType.BOX, enumPackageType.PALLET].map((type) =>
        this.translateService.get('PACKAGE_TYPE.' + enumPackageType[type]).pipe(map((label) => ({ label, value: type }))),
      ),
    )
      .pipe(take(1))
      .subscribe((select) => (this.typeSelect = select));
  }

  private getPalletTypeSelect(): void {
    forkJoin(
      [enumPalletStandard.GMA, enumPalletStandard.EUR_1, enumPalletStandard.EUR_2, enumPalletStandard.OTHER].map((type) =>
        this.translateService.get('PALLET_TYPES.' + enumPalletStandard[type]).pipe(map((label) => ({ label, value: type }))),
      ),
    )
      .pipe(take(1))
      .subscribe((select) => (this.palletStandardSelect = select));
  }

  private setDimensions(val: enumPalletStandard): void {
    if (val === enumPalletStandard.OTHER) {
      this.formGroup.get('dimensions').patchValue({ length: null, width: null, height: null });
    }

    if (val === enumPalletStandard.GMA) {
      this.formGroup.get('lengthUnit').setValue(enumLengthUnit.INCH);
      if (this.formGroup.get('lengthUnit').value === enumLengthUnit.INCH) {
        this.formGroup.get('dimensions').patchValue({ length: PALLET_GMA_IMPERIAL_LENGTH, width: PALLET_GMA_IMPERIAL_WIDTH });
      } else {
        this.formGroup.get('dimensions').patchValue({ length: PALLET_GMA_METRIC_LENGTH, width: PALLET_GMA_METRIC_WIDTH });
      }
    }

    if (val === enumPalletStandard.EUR_1) {
      this.formGroup.get('lengthUnit').setValue(enumLengthUnit.CENTIMETER);
      if (this.formGroup.get('lengthUnit').value === enumLengthUnit.INCH) {
        this.formGroup.get('dimensions').patchValue({ length: PALLET_EUR_1_IMPERIAL_LENGTH, width: PALLET_EUR_1_IMPERIAL_WIDTH });
      } else {
        this.formGroup.get('dimensions').patchValue({ length: PALLET_EUR_1_METRIC_LENGTH, width: PALLET_EUR_1_METRIC_WIDTH });
      }
    }

    if (val === enumPalletStandard.EUR_2) {
      this.formGroup.get('lengthUnit').setValue(enumLengthUnit.CENTIMETER);
      if (this.formGroup.get('lengthUnit').value === enumLengthUnit.INCH) {
        this.formGroup.get('dimensions').patchValue({ length: PALLET_EUR_2_IMPERIAL_LENGTH, width: PALLET_EUR_2_IMPERIAL_WIDTH });
      } else {
        this.formGroup.get('dimensions').patchValue({ length: PALLET_EUR_2_METRIC_LENGTH, width: PALLET_EUR_2_METRIC_WIDTH });
      }
    }
  }
}
